import {
  ArrowDown,
  ArrowUp,
  Gear,
  PencilSimple,
  Plus,
  Sparkle,
  Trash,
  Warning,
} from "@phosphor-icons/react";
import { type FormEvent, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import type { SelectionAction } from "~/api/aiAdminTypes";
import {
  useAiActionList,
  useAiProviderStatus,
  useCreateAiAction,
  useDeleteAiAction,
  useReorderAiActions,
  useUpdateAiAction,
} from "~/api/queries";
import { Pill, Row, Rows } from "~/features/automation/components";
import { Select, TextArea } from "~/features/settings/controls";
import {
  DRAWER_POSITIONS,
  type DrawerPosition,
  THOUGHT_DISPLAY_MODES,
  type ThoughtDisplayMode,
  useAiActionPreferences,
} from "~/features/settings/sections/aiActionPreferences";
import { Button } from "~/foundation/components/Button";
import { ConfirmDialog, Dialog } from "~/foundation/components/Dialog";
import { Empty } from "~/foundation/components/Empty";
import { IconButton } from "~/foundation/components/IconButton";
import { Input } from "~/foundation/components/Input";
import { Spinner } from "~/foundation/components/Spinner";
import { Switch } from "~/foundation/components/Switch";
import { Text } from "~/foundation/components/Text";

/** The icon names the platform's action catalogue understands. Values are the server's. */
const ICONS = [
  "zap",
  "edit",
  "compress",
  "plus",
  "help-circle",
  "briefcase",
  "star",
  "heart",
  "check",
  "search",
] as const;

function thoughtDisplayLabel(mode: ThoughtDisplayMode, t: (key: string) => string): string {
  switch (mode) {
    case "verbose":
      return t("Everything it thinks");
    case "summary":
      return t("A running summary");
    case "minimal":
      return t("Just that it is working");
    default:
      return t("Nothing");
  }
}

function drawerPositionLabel(position: DrawerPosition, t: (key: string) => string): string {
  switch (position) {
    case "start":
      return t("Leading edge");
    case "end":
      return t("Trailing edge");
    case "bottom":
      return t("Bottom");
    default:
      return t("Wherever it fits");
  }
}

/**
 * The provider gate, shown before anything here can be used rather than after it fails.
 *
 * It fails OPEN by construction: the adapter reports "not configured" only for an explicit
 * refusal, so this banner never appears in front of a working AI. The two variants stay
 * visually and textually distinct — "an admin must set a key" is a different problem from
 * "the service is momentarily down, try again".
 */
function ProviderGate() {
  const { t } = useTranslation();
  const { data: status } = useAiProviderStatus();

  if (!status || (status.configured && status.reachable)) {
    return null;
  }

  const outage = status.configured && !status.reachable;
  const Icon = outage ? Warning : Gear;

  return (
    <div className="flex items-start gap-3 rounded-md border border-divider bg-warning/12 px-3 py-2.5">
      <Icon size={18} aria-hidden className="mt-0.5 shrink-0 text-text-secondary" />
      <div className="min-w-0">
        <p className="text-body font-medium text-text">
          {outage ? t("AI service unavailable") : t("No AI provider is configured")}
        </p>
        <p className="text-small text-text-secondary">
          {outage
            ? t("The AI service could not be reached. Check that it is running, then try again.")
            : t(
                "No provider key is set for this environment, so these actions cannot run yet. An administrator sets one on the server.",
              )}
        </p>
      </div>
    </div>
  );
}

/** Create or edit one prompt-backed action. Built-ins never reach this — they are code. */
function ActionDialog({
  action,
  nextOrder,
  onClose,
}: {
  action: SelectionAction | null;
  nextOrder: number;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const create = useCreateAiAction();
  const update = useUpdateAiAction();
  const titleRef = useRef<HTMLInputElement>(null);

  const [title, setTitle] = useState(action?.title ?? "");
  const [prompt, setPrompt] = useState(action?.prompt ?? "");
  const [icon, setIcon] = useState(action?.icon ?? "zap");
  const [shortcut, setShortcut] = useState(action?.shortcut ?? "");
  const [showInMenu, setShowInMenu] = useState(action?.showInMenu ?? true);

  useEffect(() => {
    titleRef.current?.focus();
  }, []);

  const busy = create.isPending || update.isPending;
  const valid = title.trim().length > 0 && prompt.trim().length > 0;

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (!valid) {
      return;
    }
    const draft = {
      title: title.trim(),
      prompt: prompt.trim(),
      icon,
      shortcut: shortcut.trim() || null,
      showInMenu,
      sortOrder: action?.sortOrder ?? nextOrder,
    };
    const done = {
      onSuccess: () => {
        toast.success(action ? t("Action updated") : t("Action created"));
        onClose();
      },
    };
    if (action) {
      update.mutate({ id: action.id, draft }, done);
    } else {
      create.mutate(draft, done);
    }
  };

  return (
    <Dialog
      open
      onOpenChange={(open) => !open && onClose()}
      title={action ? t("Edit action") : t("New action")}
      closeLabel={t("Close")}
      size="md"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t("Cancel")}
          </Button>
          <Button variant="primary" disabled={busy || !valid} onClick={submit}>
            {action ? t("Save changes") : t("Create action")}
          </Button>
        </>
      }
    >
      <form onSubmit={submit} className="flex flex-col gap-3">
        <Input
          ref={titleRef}
          label={t("Name")}
          value={title}
          fullWidth
          placeholder={t("e.g. Translate to Hebrew")}
          onChange={(event) => setTitle(event.target.value)}
        />
        <TextArea
          label={t("Prompt")}
          value={prompt}
          rows={5}
          placeholder={t("What should happen to the selected text?")}
          onChange={(event) => setPrompt(event.target.value)}
        />
        <div className="grid gap-3 sm:grid-cols-2">
          <Select
            label={t("Icon")}
            value={icon}
            fullWidth
            onChange={(event) => setIcon(event.target.value)}
          >
            {ICONS.map((name) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </Select>
          <Input
            label={t("Shortcut")}
            value={shortcut}
            fullWidth
            placeholder={t("e.g. cmd+shift+t")}
            onChange={(event) => setShortcut(event.target.value)}
          />
        </div>
        <Switch
          checked={showInMenu}
          onCheckedChange={setShowInMenu}
          label={t("Show in the actions menu")}
        />
      </form>
    </Dialog>
  );
}

/**
 * What the AI offers where text is selected.
 *
 * Two kinds of row, and the difference is enforced by the server, not by this page: **built-in**
 * actions are code and can only be hidden, **your own** are rows you can write, reorder and
 * delete. Hiding a built-in is a real preference, so it is offered — deleting one is not, so it
 * is not, rather than being offered and refused.
 */
export function AiActions() {
  const { t } = useTranslation();
  const { data: actions, isLoading } = useAiActionList();
  const update = useUpdateAiAction();
  const remove = useDeleteAiAction();
  const reorder = useReorderAiActions();

  const thoughtDisplay = useAiActionPreferences((state) => state.thoughtDisplay);
  const drawerPosition = useAiActionPreferences((state) => state.drawerPosition);
  const setThoughtDisplay = useAiActionPreferences((state) => state.setThoughtDisplay);
  const setDrawerPosition = useAiActionPreferences((state) => state.setDrawerPosition);

  const [editing, setEditing] = useState<SelectionAction | null>(null);
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState<SelectionAction | null>(null);

  if (isLoading) {
    return <Spinner label={t("Loading")} />;
  }

  const mine = (actions ?? []).filter((action) => !action.isBuiltIn);
  const builtIn = (actions ?? []).filter((action) => action.isBuiltIn);

  /** Swap two neighbours' sort orders — the smallest honest edit of an ordering. */
  const move = (index: number, delta: number) => {
    const action = mine[index];
    const neighbour = mine[index + delta];
    if (!action || !neighbour) {
      return;
    }
    reorder.mutate([
      { id: action.id, sortOrder: neighbour.sortOrder },
      { id: neighbour.id, sortOrder: action.sortOrder },
    ]);
  };

  return (
    <div className="flex flex-col gap-5">
      <ProviderGate />

      <Text variant="small">
        {t("These appear when you select text in a document, and act on the selection.")}
      </Text>

      <section className="flex flex-col gap-2">
        <div className="flex items-center justify-between gap-3">
          <h3 className="text-body font-medium text-text">{t("Your actions")}</h3>
          <Button size="sm" variant="primary" icon={Plus} onClick={() => setCreating(true)}>
            {t("New action")}
          </Button>
        </div>

        {mine.length === 0 ? (
          <Empty
            icon={Sparkle}
            message={t("No actions of your own yet. Create one with the prompt you keep retyping.")}
          />
        ) : (
          <Rows>
            {mine.map((action, index) => (
              <Row key={action.id}>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-body text-text">{action.title}</p>
                  <p className="truncate text-small text-text-tertiary">{action.prompt}</p>
                </div>
                {action.shortcut ? <Pill>{action.shortcut}</Pill> : null}
                <IconButton
                  icon={ArrowUp}
                  aria-label={t("Move up")}
                  size="sm"
                  disabled={index === 0 || reorder.isPending}
                  onClick={() => move(index, -1)}
                />
                <IconButton
                  icon={ArrowDown}
                  aria-label={t("Move down")}
                  size="sm"
                  disabled={index === mine.length - 1 || reorder.isPending}
                  onClick={() => move(index, 1)}
                />
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={update.isPending}
                  onClick={() =>
                    update.mutate({
                      id: action.id,
                      draft: {
                        title: action.title,
                        prompt: action.prompt,
                        icon: action.icon,
                        shortcut: action.shortcut,
                        showInMenu: !action.showInMenu,
                        sortOrder: action.sortOrder,
                      },
                    })
                  }
                >
                  {action.showInMenu ? t("Hide") : t("Show")}
                </Button>
                <IconButton
                  icon={PencilSimple}
                  aria-label={t("Edit")}
                  size="sm"
                  onClick={() => setEditing(action)}
                />
                <IconButton
                  icon={Trash}
                  aria-label={t("Delete")}
                  size="sm"
                  onClick={() => setDeleting(action)}
                />
              </Row>
            ))}
          </Rows>
        )}
      </section>

      <section className="flex flex-col gap-2">
        <h3 className="text-body font-medium text-text">{t("Built-in actions")}</h3>
        <Text variant="small">
          {t("These come with the platform. You can hide one, but not change or delete it.")}
        </Text>
        <Rows>
          {builtIn.map((action) => (
            <Row key={action.id}>
              <div className="min-w-0 flex-1">
                <p className="truncate text-body text-text">{action.title}</p>
                <p className="truncate text-small text-text-tertiary">{action.prompt}</p>
              </div>
              {action.shortcut ? <Pill>{action.shortcut}</Pill> : null}
              <Pill>{t("Built in")}</Pill>
            </Row>
          ))}
        </Rows>
      </section>

      <section className="flex flex-col gap-2">
        <h3 className="text-body font-medium text-text">{t("How results are shown")}</h3>
        <Text variant="small">
          {/* No endpoint exists for these; saying so beats implying a workspace setting. */}
          {t("Saved on this device only.")}
        </Text>
        <div className="grid gap-3 sm:grid-cols-2">
          <Select
            label={t("Reasoning shown while it works")}
            value={thoughtDisplay}
            fullWidth
            onChange={(event) => setThoughtDisplay(event.target.value as ThoughtDisplayMode)}
          >
            {THOUGHT_DISPLAY_MODES.map((mode) => (
              <option key={mode} value={mode}>
                {thoughtDisplayLabel(mode, t)}
              </option>
            ))}
          </Select>
          <Select
            label={t("Where results open")}
            value={drawerPosition}
            fullWidth
            onChange={(event) => setDrawerPosition(event.target.value as DrawerPosition)}
          >
            {DRAWER_POSITIONS.map((position) => (
              <option key={position} value={position}>
                {drawerPositionLabel(position, t)}
              </option>
            ))}
          </Select>
        </div>
      </section>

      {creating ? (
        <ActionDialog
          action={null}
          nextOrder={mine.length + 1}
          onClose={() => setCreating(false)}
        />
      ) : null}
      {editing ? (
        <ActionDialog
          action={editing}
          nextOrder={mine.length + 1}
          onClose={() => setEditing(null)}
        />
      ) : null}
      {deleting ? (
        <ConfirmDialog
          open
          onOpenChange={(open) => !open && setDeleting(null)}
          title={t("Delete {{name}}?", { name: deleting.title })}
          description={t("This cannot be undone.")}
          confirmLabel={t("Delete")}
          cancelLabel={t("Cancel")}
          closeLabel={t("Close")}
          dangerous
          busy={remove.isPending}
          onConfirm={() =>
            remove.mutate(deleting.id, {
              onSuccess: () => {
                toast.success(t("Action deleted"));
                setDeleting(null);
              },
            })
          }
        />
      ) : null}
    </div>
  );
}
